import {NUM_CHANCES} from "modules/constants";
import {IStore} from "modules/store";
import {createSelector} from "@reduxjs/toolkit";
import {ContestStatus} from "modules/enums";
import {eq, filter, get, memoize} from "lodash";
import {NullOrEmpty} from "modules/types";
import {parseStateType} from "modules/utils/helpers";

const getState = ({contest}: IStore) => contest;

export const getContests = createSelector(getState, (state) => state.contests);
export const getActiveContest = createSelector(getContests, (contests) =>
	contests.find((item) => item.status === ContestStatus.Active)
);
export const getContestExternalId = createSelector(getActiveContest, (contest) => {
	if (!contest) {
		return null;
	}

	return contest.mlbContestId;
});
export const getActiveContestCategory = createSelector(getActiveContest, (contest) => {
	return contest?.category ?? "empty";
});
export const getActiveContestTranslationCategory = createSelector(getActiveContest, (contest) => {
	const category = contest?.category ?? "error";
	return `game.gameplay.${category}`;
});

export const getCurrentQuestion = createSelector(getState, (state) => state.currentQuestion);
export const getCurrentQuestionIndex = createSelector(
	getState,
	(state) => state.currentQuestionIndex ?? 0
);

export const getGamePlayers = createSelector(getState, (state) => state.playersCardsIds);
export const getCurrentPlayers = createSelector(
	getGamePlayers,
	getCurrentQuestionIndex,
	(players, index) => {
		const firstPlayerId = players[index];
		const secondPlayerId = players[index + 1];

		return {
			firstPlayerId,
			secondPlayerId,
			playersArray: players,
		};
	}
);

export const getIsGameStarted = createSelector(getState, (state) => state.isGameStarted);
export const getPlayersStatsMapper = createSelector(getState, (state) => state.playerStatsMapper);
export const getStatByPlayerId = createSelector(getPlayersStatsMapper, (stats) => {
	return memoize((id: NullOrEmpty<number> = 0) => {
		return stats[id || 0];
	});
});

export const getIsTimerVisible = createSelector(getState, (state) => state.isTimerVisible);
export const getTimerTime = createSelector(getState, (state) => state.timerTime);
export const getCurrentTimerTimeLimit = createSelector(
	getState,
	(state) => state.currentTimerTimeLimit
);

export const getIsButtonsDisabled = createSelector(getState, (state) => state.isButtonsDisabled);

export const getContestResult = createSelector(getState, (state) => state.contestResult);
export const getContestResultRequestState = createSelector(getState, (state) =>
	parseStateType(state.requestStates.showMy)
);
export const getContestCorrectAnswers = createSelector(
	getContestResult,
	getActiveContest,
	(result, activeContest) => {
		if (!result || result?.contestId !== activeContest?.id) {
			return null;
		}

		return result.predictions.filter((prediction) => prediction.isCorrect === true).length;
	}
);

export const getIsSummaryShouldBeShown = createSelector(
	getContestResult,
	getIsGameStarted,
	(contestResult, isGameStarted) => {
		const predictions = get(contestResult, "predictions", []);
		const inCorrectPredictions = filter(predictions, (prd) => eq(prd.isCorrect, false));
		return eq(inCorrectPredictions.length, NUM_CHANCES) && !isGameStarted;
	}
);

export const getContestGameBar = createSelector(getContestResult, (result) => result?.gameBar);

export const getIsContestCleared = createSelector(getState, (state) => state.isContestCleared);
